import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface DateRangeFilterProps {
  onDateChange: (startDate: Date | null, endDate: Date | null) => void;
}

export default function DateRangeFilter({ onDateChange }: DateRangeFilterProps) {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const handlePreset = (days: number | null) => {
    if (days === null) {
      // Todo período
      setStartDate(null);
      setEndDate(null);
      onDateChange(null, null);
    } else {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - days);
      setStartDate(start);
      setEndDate(end);
      onDateChange(start, end);
    }
  };

  const handleStartDateChange = (date: Date | undefined) => {
    const newStart = date || null;
    setStartDate(newStart);
    onDateChange(newStart, endDate);
  };

  const handleEndDateChange = (date: Date | undefined) => {
    const newEnd = date || null;
    setEndDate(newEnd);
    onDateChange(startDate, newEnd);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePreset(7)}
          className="text-xs sm:text-sm"
        >
          Últimos 7 dias
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePreset(30)}
          className="text-xs sm:text-sm"
        >
          Últimos 30 dias
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const now = new Date();
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            setStartDate(start);
            setEndDate(now);
            onDateChange(start, now);
          }}
          className="text-xs sm:text-sm"
        >
          Este mês
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePreset(null)}
          className="text-xs sm:text-sm"
        >
          Todo período
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label className="text-sm font-medium mb-2 block">Data Início</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !startDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, "dd/MM/yyyy") : "Selecionar data"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate || undefined}
                onSelect={handleStartDateChange}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex-1">
          <label className="text-sm font-medium mb-2 block">Data Fim</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !endDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "dd/MM/yyyy") : "Selecionar data"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate || undefined}
                onSelect={handleEndDateChange}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
}
